import { ChangeDetectionStrategy, Component, computed, DestroyRef, ElementRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { NotificationService } from '@services';
import { SasNotificationsRouterService } from '@apas-one/common/sas-notifications/sas-notifications-router.service';
import { SasNotificationsService } from '@apas-one/common/sas-notifications/sas-notifications.service';
import { SasButtonComponent, SasRelatedItemComponent } from '@components';
import { AdminUserNotification, NotificationApplicationParameters } from './admin-user-notification';
import { NotificationType } from './notification-type';

// Readable fallback while a notification type has no translation.
const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
    [NotificationType.OfficerReportGenerated]: 'Officer report generated'
};

@Component({
    selector: 'sas-notifications',
    standalone: true,
    imports: [TranslatePipe, SasButtonComponent, SasRelatedItemComponent],
    templateUrl: './sas-notifications.component.html',
    styleUrl: './sas-notifications.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '(document:keydown.escape)': 'closePanel()',
        '(document:click)': 'onDocumentClick($event)'
    }
})
export class SasNotificationsComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly notificationsService = inject(SasNotificationsService);
    private readonly notificationRouter = inject(SasNotificationsRouterService);
    private readonly notificationError = inject(NotificationService);
    private readonly translate = inject(TranslateService);

    readonly notifications = signal<AdminUserNotification[]>([]);
    readonly panelOpen = signal(false);
    // After the first load the previous list stays visible while it refreshes.
    readonly loaded = signal(false);
    readonly planningSubmissionsReceived = signal(0);
    readonly pendingSignatureReceived = signal(0);
    readonly pendingCitizenComments = signal(0);
    readonly pendingConsulteeComments = signal(0);
    readonly listTop = signal(0);

    // Socket flag is only trusted until the list is loaded or changed locally.
    private readonly socketHasPending = signal(false);
    readonly hasPending = computed(() =>
        this.notifications().length > 0 || this.planningSubmissionsReceived() > 0
        || this.pendingSignatureReceived() > 0 || this.pendingCitizenComments() > 0
        || this.pendingConsulteeComments() > 0
    );
    readonly hasNotifications = computed(() => this.socketHasPending() || this.hasPending());

    constructor() {
        this.destroyRef.onDestroy(() => this.notificationsService.disconnect());
    }

    ngOnInit(): void {
        this.listenSocket();
        this.notificationsService.connect();
    }

    togglePanel(): void {
        this.panelOpen.update(open => !open);
        if (this.panelOpen()) {
            // List is fixed to the right edge of the viewport, starting just below the bell.
            this.listTop.set(this.host.nativeElement.getBoundingClientRect().bottom);
            this.loadNotifications();
        }
    }

    closePanel(): void {
        this.panelOpen.set(false);
    }

    // Outside-click close without a backdrop, so the page keeps scrolling while open.
    onDocumentClick(event: MouseEvent): void {
        if (this.panelOpen() && !this.host.nativeElement.contains(event.target as Node)) {
            this.closePanel();
        }
    }

    // Navigate right away; marking as read runs in the background so the user doesn't wait for it.
    openNotification(notification: AdminUserNotification): void {
        this.markAsRead(notification);
        this.closePanel();
        this.notificationRouter.navigateToSource(notification);
    }

    dismiss(notification: AdminUserNotification, event: Event): void {
        event.stopPropagation();
        this.markAsRead(notification, () => {
            if (!this.hasPending()) {
                this.closePanel();
            }
        });
    }

    // Destination not defined yet: it only clears the item until the next load.
    openPlanningSubmissions(): void {
        this.planningSubmissionsReceived.set(0);
        this.closePanel();
    }

    openPendingSignatures(): void {
        this.pendingSignatureReceived.set(0);
        this.closePanel();
        this.notificationRouter.navigateToPendingSignatures();
    }

    openPendingCitizenComments(): void {
        this.closePanel();
        this.notificationRouter.navigateToPendingComments('citizen');
    }

    openPendingConsulteeComments(): void {
        this.closePanel();
        this.notificationRouter.navigateToPendingComments('consultee');
    }

    typeLabel(type: string): string {
        return NOTIFICATION_TYPE_LABELS[type] ?? type;
    }

    /** Readable subtitle based on the notification type parameters. */
    getSubtitle(notification: AdminUserNotification): string {
        return (notification.typeParameters as NotificationApplicationParameters)?.applicationReference ?? '';
    }

    /** Returns "Today", "Yesterday" or the short date. */
    formatDate(isoDate: string): string {
        const date = new Date(isoDate);
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);

        if (this.isSameDay(date, today)) return this.translate.instant('TODAY', { Default: 'Today' });
        if (this.isSameDay(date, yesterday)) return this.translate.instant('YESTERDAY', { Default: 'Yesterday' });

        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    }

    private isSameDay(a: Date, b: Date): boolean {
        return a.getFullYear() === b.getFullYear()
            && a.getMonth() === b.getMonth()
            && a.getDate() === b.getDate();
    }

    // Not tied to destroyRef: navigating may leave the module and the read must still be saved.
    private markAsRead(notification: AdminUserNotification, onDone?: () => void): void {
        this.notificationsService.readNotification(notification.id)
            .subscribe({
                next: () => {
                    this.notifications.update(list => list.filter(n => n.id !== notification.id));
                    this.socketHasPending.set(false);
                    onDone?.();
                },
                error: (err) => this.notificationError.error(err, 'UNHANDLED_ERROR')
            });
    }

    private loadNotifications(): void {
        this.notificationsService.getNotifications()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    this.notifications.set(response?.notifications ?? []);
                    this.planningSubmissionsReceived.set(response?.planningSubmissionsReceived ?? 0);
                    this.pendingSignatureReceived.set(response?.pendingSignature ?? 0);
                    this.pendingCitizenComments.set(response?.pendingCitizenComments ?? 0);
                    this.pendingConsulteeComments.set(response?.pendingConsulteeComments ?? 0);
                    this.socketHasPending.set(false);
                    this.loaded.set(true);
                },
                error: (err) => this.notificationError.error(err, 'UNHANDLED_ERROR')
            });
    }

    private listenSocket(): void {
        this.notificationsService.messages$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((message) => {
                this.socketHasPending.set(message.hasNotifications);
                // If the panel is open, refresh the list when something new arrives.
                if (message.hasNotifications && this.panelOpen()) {
                    this.loadNotifications();
                }
            });
    }
}
